import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminStaff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    hourly_rate: 0,
    role: "staff",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setStaff(data.filter((u) => u.role === "staff" || u.role === "admin"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startNew = (role = "staff") => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", hourly_rate: 0, role });
    setOpen(true);
  };
  const startEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      email: s.email,
      password: "",
      hourly_rate: s.hourly_rate,
      role: s.role || "staff",
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        const payload = {
          name: form.name,
          email: form.email,
          hourly_rate: Number(form.hourly_rate),
        };
        if (form.password) payload.password = form.password;
        if (user?.is_super_admin) payload.role = form.role;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/users", {
          name: form.name,
          email: form.email,
          password: form.password,
          hourly_rate: Number(form.hourly_rate),
          role: form.role,
        });
        toast.success(form.role === "admin" ? "Admin created" : "Staff created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (member) => {
    const memberType = member.role === "admin" ? "admin" : "staff member";
    if (!window.confirm(`Delete this ${memberType}?`)) return;
    try {
      await api.delete(`/users/${member.id}`);
      toast.success(`${member.role === "admin" ? "Admin" : "Staff member"} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1200px]">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Staff</div>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
            The team.
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => startNew("staff")}
            className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white"
            data-testid="new-staff-btn"
          >
            <Plus size={16} className="mr-1" /> New staff
          </Button>
          <Button
            onClick={() => startNew("admin")}
            variant="outline"
            className="rounded-sm"
            data-testid="new-admin-btn"
          >
            <Plus size={16} className="mr-1" /> New admin
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="rounded-md">
              <DialogHeader>
                <DialogTitle className="font-display">
                  {editing ? "Edit team member" : "Add team member"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Name</Label>
                  <Input
                    className="mt-2 rounded-sm"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="staff-name-input"
                  />
                </div>
                {(!editing || user?.is_super_admin) && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider">Role</Label>
                    <select
                      className="mt-2 w-full rounded-sm border border-[#E5E5E5] bg-white px-3 py-2 text-sm"
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      data-testid="staff-role-select"
                    >
                      <option value="staff">Staff</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wider">Email</Label>
                  <Input
                    className="mt-2 rounded-sm"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="staff-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">
                    {editing ? "New password (leave blank to keep)" : "Password"}
                  </Label>
                  <Input
                    className="mt-2 rounded-sm"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="staff-password-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Hourly rate</Label>
                  <Input
                    className="mt-2 rounded-sm font-mono-num"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    data-testid="staff-rate-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setOpen(false)}
                  className="rounded-sm"
                  data-testid="cancel-staff-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  className="rounded-sm bg-[#0033CC] hover:bg-[#002299] text-white"
                  data-testid="save-staff-btn"
                >
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F3F2]">
            <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Hourly rate</th>
              <th className="px-4 py-3 w-40" />
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-neutral-500">
                  No staff yet. Add your first team member.
                </td>
              </tr>
            ) : (
              staff.map((s) => (
                <tr key={s.id} className="border-t border-[#E5E5E5]" data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{s.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        s.role === "admin"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-neutral-100 text-neutral-700"
                      }`}
                    >
                      {s.role === "admin" ? "Admin" : "Staff"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num">${Number(s.hourly_rate).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      {!s.is_super_admin && (
                        <button
                          onClick={() => startEdit(s)}
                          className="text-neutral-500 hover:text-[#0033CC] transition-colors"
                          aria-label={`Edit ${s.name}`}
                          data-testid={`edit-staff-btn-${s.id}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      {s.id !== user?.id && !s.is_super_admin &&
                        (s.role !== "admin" || user?.is_super_admin) && (
                          <button
                            onClick={() => remove(s)}
                            className="text-neutral-500 hover:text-[#D82C0D] transition-colors"
                            aria-label={`Delete ${s.name}`}
                            data-testid={`delete-staff-btn-${s.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
